import {useState, useCallback, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {TState} from '../redux/store.type';
import {getEventSettings} from '../redux/event/eventActions';
import {UseSendDataConfig, SendData} from './sendHooks.types';
import {useDefaultFbImage} from './useDefaultFbImage';
import {serializeTemplateStructure} from '../component/whatsAppTemplateEditor/utils/formatUtils';
import {TemplateStructure} from '../component/whatsAppTemplateEditor/types/templateStructure';

type WhatsappEdits = Partial<{
    image: {path: string, imageId: string};
    message: string | null;
    whatsappTemplate: TemplateStructure | null;
}>;

export const useSendData = ({defaultSubject, defaultEmailBody, defaultSmsMessage, type, hasWhatsapp = true}: UseSendDataConfig) => {
    const savedData = useSelector((s: TState) => type ? s.mailing.designMailing?.[type] : null);
    const getEventSettingsXHR = useSelector((s: TState) => s.event.getEventSettingsXHR);
    const brandingWhatsappTemplate = hasWhatsapp && type ? useSelector((s: TState) => s.branding[`${type}WhatsappTemplate`]) : null;
    const whatsappTemplate = hasWhatsapp ? brandingWhatsappTemplate : null;
    const defaultWhatsappImage = useDefaultFbImage(true);
    const defaultEmailImage = useDefaultFbImage(false);

    const [whatsappEdits, setWhatsappEdits] = useState<WhatsappEdits>({});

    const whatsappData = useMemo(() => {
        let message: string | null = null;
        let template: TemplateStructure | null = null;

        if(hasWhatsapp && savedData?.whatsappContent) {
            const parsed = JSON.parse(savedData.whatsappContent);
            if(Array.isArray(parsed)) {
                template = parsed as TemplateStructure;
                message = serializeTemplateStructure(template);
            }
        } else if(hasWhatsapp && whatsappTemplate) {
            template = whatsappTemplate as unknown as TemplateStructure;
            message = serializeTemplateStructure(template);
        }

        return {
            image: whatsappEdits.image ?? defaultWhatsappImage,
            message: whatsappEdits.message ?? message,
            whatsappTemplate: whatsappEdits.whatsappTemplate ?? template
        };
    }, [whatsappEdits, defaultWhatsappImage, savedData?.whatsappContent, whatsappTemplate, hasWhatsapp]);

    const [emailData, setEmailData] = useState({
        image: defaultEmailImage,
        emailBody: savedData?.emailContent || defaultEmailBody,
        emailSubject: savedData?.emailSubject || defaultSubject
    });

    const [smsData, setSmsData] = useState({message: savedData?.smsContent || defaultSmsMessage});

    const handleDataSave = useCallback((data: unknown, key: string, channel: keyof SendData) => {
        if(channel === 'whatsapp') {
            setWhatsappEdits(prev => ({...prev, [key]: data}));
            return;
        }
        const setters = {email: setEmailData, sms: setSmsData};
        const setter = setters[channel];
        if(setter) {
            setter(prev => ({...prev, [key]: data}));
        }
    }, []);

    useEffect(() => {
        if(!getEventSettingsXHR) {
            getEventSettings().then();
        }
    }, [getEventSettingsXHR]);

    useEffect(() => {
        if(defaultEmailImage.path !== emailData.image.path) {
            setEmailData(prev => ({...prev, image: defaultEmailImage}));
        }
    }, [defaultEmailImage.path, emailData.image.path]);

    useEffect(() => {
        if(!savedData?.whatsappContent) return;
        setWhatsappEdits(prev => {
            const {message, whatsappTemplate, ...rest} = prev;
            return rest;
        });
    }, [savedData?.whatsappContent]);

    useEffect(() => {
        if(!savedData) return;
        if(savedData.whatsappContent)
            setWhatsappEdits(prev => {
                const {message, whatsappTemplate, ...rest} = prev;
                return rest;
            });
        if(savedData.emailContent) {
            setEmailData(prev => ({
                ...prev,
                emailBody: savedData.emailContent!,
                ...(savedData.emailSubject ? {emailSubject: savedData.emailSubject} : {})
            }));
        }
        if(savedData.smsContent) {
            setSmsData({message: savedData.smsContent});
        }
    }, [savedData]);

    return {whatsappData, emailData, smsData, handleDataSave, whatsappTemplate};
};
